// One emoji glyph: a flag (two regional indicators), a keycap ("1️⃣": digit,
// # or * followed by the combining keycap mark) or a pictographic base with
// its optional variation selector / skin tone, possibly extended with
// ZWJ-joined glyphs ("👨‍🚀"). Unicode property escapes cover far more than the
// display regex below (which only needs to catch the common ranges).
const EMOJI_GLYPH =
  '(?:\\p{Regional_Indicator}{2}|[0-9#*]\\uFE0F?\\u20E3|\\p{Extended_Pictographic}(?:\\uFE0F|\\p{Emoji_Modifier})*)';
const LEADING_EMOJI_REGEX = new RegExp(`^(${EMOJI_GLYPH}(?:\\u200D${EMOJI_GLYPH})*)`, 'u');

/**
 * Splits the emoji a text starts with from the rest of the text.
 * @param {string} text - The text that may start with an emoji.
 * @returns {object} - { emoji, rest }: the leading emoji (null when the text
 * doesn't start with one) and the remaining text, trimmed.
 * @example
 * splitLeadingEmoji('🎬 Cinéma'); // { emoji: '🎬', rest: 'Cinéma' }
 */
export const splitLeadingEmoji = text => {
  if (!text || typeof text !== 'string') {
    return { emoji: null, rest: text };
  }
  const trimmed = text.trim();
  const match = trimmed.match(LEADING_EMOJI_REGEX);
  if (!match) {
    return { emoji: null, rest: text };
  }
  return { emoji: match[1], rest: trimmed.slice(match[1].length).trim() };
};

/**
 * Wraps emojis in a span with a special class and returns JSX elements
 * @param {string} text - The text that may contain emojis
 * @returns {Array} - Array of JSX elements with emojis wrapped in spans
 */
export const wrapEmojisJSX = text => {
  if (!text || typeof text !== 'string') {
    return text;
  }

  // Emoji regex pattern - matches most Unicode emojis
  const emojiRegex = /[\u{1F600}-\u{1F64F}]|[\u{1F300}-\u{1F5FF}]|[\u{1F680}-\u{1F6FF}]|[\u{1F1E0}-\u{1F1FF}]|[\u{2600}-\u{26FF}]|[\u{2700}-\u{27BF}]|[\u{1F900}-\u{1F9FF}]|[\u{1F018}-\u{1F270}]|[\u{238C}-\u{2454}]|[\u{20D0}-\u{20FF}]|[\u{FE0F}]|[\u{1F3FB}-\u{1F3FF}]|[\u{1F9B0}-\u{1F9B3}]|[\u{1F9D1}-\u{1F9DD}]|[\u{1F9E0}-\u{1F9FF}]|[\u{1F004}]|[\u{1F0CF}]|[\u{1F170}-\u{1F251}]|[\u{1F300}-\u{1F321}]|[\u{1F324}-\u{1F393}]|[\u{1F396}-\u{1F397}]|[\u{1F399}-\u{1F39B}]|[\u{1F39E}-\u{1F3F0}]|[\u{1F3F3}-\u{1F3F5}]|[\u{1F3F7}-\u{1F3FA}]|[\u{1F400}-\u{1F4FD}]|[\u{1F4FF}-\u{1F53D}]|[\u{1F549}-\u{1F54E}]|[\u{1F550}-\u{1F567}]|[\u{1F56F}-\u{1F570}]|[\u{1F573}-\u{1F57A}]|[\u{1F587}]|[\u{1F58A}-\u{1F58D}]|[\u{1F590}]|[\u{1F595}-\u{1F596}]|[\u{1F5A4}-\u{1F5A5}]|[\u{1F5A8}]|[\u{1F5B1}-\u{1F5B2}]|[\u{1F5BC}]|[\u{1F5C2}-\u{1F5C4}]|[\u{1F5D1}-\u{1F5D3}]|[\u{1F5DC}-\u{1F5DE}]|[\u{1F5E1}]|[\u{1F5E3}]|[\u{1F5E8}]|[\u{1F5EF}]|[\u{1F5F3}]|[\u{1F5FA}-\u{1F64F}]|[\u{1F680}-\u{1F6C5}]|[\u{1F6CB}-\u{1F6D2}]|[\u{1F6E0}-\u{1F6E5}]|[\u{1F6E9}]|[\u{1F6EB}-\u{1F6EC}]|[\u{1F6F0}]|[\u{1F6F3}-\u{1F6F9}]|[\u{1F910}-\u{1F93A}]|[\u{1F93C}-\u{1F93E}]|[\u{1F940}-\u{1F945}]|[\u{1F947}-\u{1F970}]|[\u{1F973}-\u{1F976}]|[\u{1F97A}]|[\u{1F97C}-\u{1F9A2}]|[\u{1F9B0}-\u{1F9B9}]|[\u{1F9C0}-\u{1F9C2}]|[\u{1F9D0}-\u{1F9FF}]/gu;

  const parts = text.split(emojiRegex);
  const result = [];

  for (let i = 0; i < parts.length; i++) {
    if (parts[i]) {
      result.push(parts[i]);
    }

    if (i < parts.length - 1) {
      const emojiMatch = text.match(emojiRegex);
      if (emojiMatch && emojiMatch[i]) {
        result.push(
          <span key={`emoji-${i}`} class="emoji-no-invert">
            {emojiMatch[i]}
          </span>
        );
      }
    }
  }

  return result.length > 0 ? result : text;
};
